import { RadioStepOption } from '@f/applications/QuizApp/partials/composeTileOptions'
import {
  baseSizeOptions,
  largeSizeOptions,
  smallSizeOptions
} from '@f/applications/QuizApp/questions/QuestionLivingArea'
import { HouseSizeLIcon } from '@f/elements/Icon/HouseSizeLIcon'
import { HouseSizeMIcon } from '@f/elements/Icon/HouseSizeMIcon'
import { HouseSizeSIcon } from '@f/elements/Icon/HouseSizeSIcon'
import { HouseSizeXSIcon } from '@f/elements/Icon/HouseSizeXSIcon'

import { ExpansionStageValue } from '@f-fertighaus/applications/Next/utils/backendStructureMappings'
import { PRICE } from '@f-fertighaus/applications/Next/utils/price'

export const composeQualityBasedBudgetRadioOptions = ({
  expansion_stage,
  living_area_total,
  customValuesForQualityBasedValues
}: {
  expansion_stage: ExpansionStageValue
  living_area_total: number
  customValuesForQualityBasedValues?: Record<ExpansionStageValue, QualityValue>
}): RadioStepOption[] => {
  const { minPrice, maxValue, comfort, luxury } = calculateQualityValues({
    expansion_stage,
    living_area_total,
    customValuesForQualityBasedValues
  })

  return [
    {
      value: minPrice,
      icon: HouseSizeXSIcon,
      label: (
        <span>
          Günstig
          <br />
          {`ca. ${PRICE.formatPrice(minPrice)}`}
        </span>
      ),
      inputProps: { id: 'min-price' }
    },
    {
      value: maxValue,
      icon: HouseSizeSIcon,
      label: (
        <span>
          Standard
          <br />
          {`ca. ${PRICE.formatPrice(maxValue)}`}
        </span>
      ),
      inputProps: { id: 'max-value' }
    },
    {
      value: comfort,
      icon: HouseSizeMIcon,
      label: (
        <span>
          Hochwertig
          <br />
          {`ca. ${PRICE.formatPrice(comfort)}`}
        </span>
      ),
      inputProps: { id: 'comfort' }
    },
    {
      value: luxury,
      icon: HouseSizeLIcon,
      label: (
        <span>
          Exklusiv
          <br />
          {`ca. ${PRICE.formatPrice(luxury)}`}
        </span>
      ),
      inputProps: { id: 'luxury' }
    }
  ]
}

export type QualityValue = {
  minPrice: number
  maxValue: number
  comfort: number
  luxury: number
}

export const QUALITY_VALUES: Record<ExpansionStageValue, QualityValue> = {
  fitting: {
    minPrice: 1600,
    maxValue: 2000,
    comfort: 2500,
    luxury: 3000
  },
  lego: {
    minPrice: 1400,
    maxValue: 1600,
    comfort: 2000,
    luxury: 2500
  },
  keyready: {
    minPrice: 1900,
    maxValue: 2500,
    comfort: 3000,
    luxury: 3500
  }
}

export function roundToTenThousand(num: number): number {
  return Math.round(num / 10_000) * 10_000
}

export const calculateQualityValues = ({
  expansion_stage,
  living_area_total,
  customValuesForQualityBasedValues
}: {
  expansion_stage: ExpansionStageValue
  living_area_total: number
  customValuesForQualityBasedValues?: Record<ExpansionStageValue, QualityValue>
}) => {
  const { minPrice, maxValue, comfort, luxury } =
    customValuesForQualityBasedValues &&
    Object.keys(customValuesForQualityBasedValues || {}).includes(
      expansion_stage
    )
      ? customValuesForQualityBasedValues[expansion_stage]
      : QUALITY_VALUES[expansion_stage]

  return {
    minPrice: roundToTenThousand(minPrice * living_area_total),
    maxValue: roundToTenThousand(maxValue * living_area_total),
    comfort: roundToTenThousand(comfort * living_area_total),
    luxury: roundToTenThousand(luxury * living_area_total)
  }
}

const isNumberRadioStepOption = (
  option: RadioStepOption
): option is RadioStepOption<number> => typeof option.value === 'number'

export const listOfRanges = [
  ...smallSizeOptions,
  ...baseSizeOptions,
  ...largeSizeOptions
].filter(isNumberRadioStepOption)

export const getLivingAreaRadioOptionByValue = (living_area_total: number) => {
  return listOfRanges.find(({ value }) => value === living_area_total)
}

export const getLivingAreaOneSizeSmallerRange = (living_area_total: number) => {
  const currentIndex = listOfRanges.findIndex(
    ({ inputProps }) =>
      Number(inputProps?.dataset?.budgetCalcValue) === living_area_total
  )

  return listOfRanges[currentIndex - 1]
}
